import React, { useState, useEffect, useRef } from 'react';
import { UserPreferences } from '../types';
import { isIOSSafari } from '../utils/deviceDetection';
import './Menu.css';

interface MenuProps {
  preferences: UserPreferences;
  onLanguageChange: () => void;
  onDarkModeToggle: () => void;
  canInstallApp: boolean;
  isAppInstalled: boolean;
  onInstallApp: () => void;
  onShowDisclaimer: () => void;
  onShowAbout: () => void;
  onShowIOSInstallInstructions?: () => void;
}

export const Menu: React.FC<MenuProps> = ({
  preferences,
  onLanguageChange,
  onDarkModeToggle,
  canInstallApp,
  isAppInstalled,
  onInstallApp,
  onShowDisclaimer,
  onShowAbout,
  onShowIOSInstallInstructions
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleLanguageClick = () => {
    onLanguageChange();
    closeMenu();
  };

  const handleDarkModeClick = () => {
    onDarkModeToggle();
    closeMenu();
  };

  const handleInstallClick = () => {
    onInstallApp();
    closeMenu();
  };

  const handleIOSInstallClick = () => {
    if (onShowIOSInstallInstructions) {
      onShowIOSInstallInstructions();
      closeMenu();
    }
  };

  const handleDisclaimerClick = () => {
    onShowDisclaimer();
    closeMenu();
  };

  const handleAboutClick = () => {
    onShowAbout();
    closeMenu();
  };

  return (
    <div className="menu-container">
      <button
        ref={buttonRef}
        className="menu-button"
        onClick={toggleMenu}
        aria-label="Open menu"
      >
        <div className="menu-dots">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
      </button>

      {/* Backdrop */}
      {isOpen && <div className="menu-backdrop" onClick={closeMenu} />}

      {/* Menu */}
      <div
        ref={menuRef}
        className={`menu-panel ${isOpen ? 'menu-panel-open' : ''}`}
      >
        <div className="menu-header">
          <h3>Settings</h3>
          <button className="menu-close" onClick={closeMenu} aria-label="Close menu">
            ×
          </button>
        </div>

        <div className="menu-content">
          <div className="menu-item">
            <div className="menu-item-icon">🌐</div>
            <div className="menu-item-content">
              <div className="menu-item-label">
                {preferences.language === 'de' ? 'Sprache' : 'Language'}
              </div>
              <select 
                className="menu-dropdown" 
                value={preferences.language} 
                onChange={(e) => {
                  if (e.target.value !== preferences.language) {
                    handleLanguageClick();
                  }
                }}
              >
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
          </div>

          <div className="menu-item">
            <div className="menu-item-icon">
              {preferences.darkMode ? '☀️' : '🌙'}
            </div>
            <div className="menu-item-content">
              <div className="menu-item-label">
                {preferences.language === 'de' ? 'Design' : 'Theme'}
              </div>
              <select 
                className="menu-dropdown" 
                value={preferences.darkMode ? 'dark' : 'light'} 
                onChange={(e) => {
                  const isDark = e.target.value === 'dark';
                  if (isDark !== preferences.darkMode) {
                    handleDarkModeClick();
                  }
                }}
              >
                <option value="light">
                  {preferences.language === 'de' ? 'Hell' : 'Light'}
                </option>
                <option value="dark">
                  {preferences.language === 'de' ? 'Dunkel' : 'Dark'}
                </option>
              </select>
            </div>
          </div>

          <div className="menu-divider"></div>

          <div 
            className={`menu-item ${isAppInstalled ? 'menu-item-disabled' : ''} ${canInstallApp || (isIOSSafari() && !isAppInstalled) ? '' : 'menu-item-disabled'}`}
            onClick={
              isAppInstalled 
                ? undefined 
                : canInstallApp 
                  ? handleInstallClick 
                  : (isIOSSafari() && onShowIOSInstallInstructions)
                    ? handleIOSInstallClick
                    : undefined
            }
          >
            <div className="menu-item-icon">📲</div>
            <div className="menu-item-content">
              <div className="menu-item-label">
                {isAppInstalled 
                  ? (preferences.language === 'de' ? 'App installiert' : 'App installed')
                  : (preferences.language === 'de' ? 'App herunterladen' : 'Download App')
                }
              </div>
              <div className="menu-item-value">
                {isAppInstalled 
                  ? (preferences.language === 'de' ? 'Bereits installiert' : 'Already installed')
                  : canInstallApp
                    ? (preferences.language === 'de' ? 'Als App installieren' : 'Install as app')
                    : isIOSSafari()
                      ? (preferences.language === 'de' ? 'Installationsanleitung' : 'Install instructions')
                      : (preferences.language === 'de' ? 'Nicht verfügbar' : 'Not available')
                }
              </div>
            </div>
          </div>

          <div className="menu-item" onClick={handleAboutClick}>
            <div className="menu-item-icon">📋</div>
            <div className="menu-item-content">
              <div className="menu-item-label">
                {preferences.language === 'de' ? 'Über' : 'About'}
              </div>
              <div className="menu-item-value">
                {preferences.language === 'de' ? 'Über Zuckerrechner' : 'About Zuckerrechner'}
              </div>
            </div>
          </div>

          <div className="menu-item" onClick={handleDisclaimerClick}>
            <div className="menu-item-icon">ℹ️</div>
            <div className="menu-item-content">
              <div className="menu-item-label">
                {preferences.language === 'de' ? 'Hinweise' : 'Notice'}
              </div>
              <div className="menu-item-value">
                {preferences.language === 'de' ? 'Wichtige Informationen' : 'Important information'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};